import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AppState } from '../types/secs';

interface AppStore extends AppState {
  setSelectedStream: (stream: string | null) => void;
  setSelectedFunction: (func: string | null) => void;
  setSearchQuery: (query: string) => void;
  toggleDarkMode: () => void;
  addToRecentlyViewed: (messageId: string) => void;
  toggleFavorite: (messageId: string) => void;
  toggleSidebar: () => void;
  setSidebarWidth: (width: number) => void;
}

const MAX_RECENT_ITEMS = 10;
const DEFAULT_SIDEBAR_WIDTH = 256;
const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 600;

export const useAppStore = create<AppStore>()(
  persist(
    (set) => ({
      selectedStream: null,
      selectedFunction: null,
      searchQuery: '',
      isDarkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
      recentlyViewed: [],
      favorites: [],
      sidebarCollapsed: false,
      sidebarWidth: DEFAULT_SIDEBAR_WIDTH,

      setSelectedStream: (stream) => set({ selectedStream: stream }),

      setSelectedFunction: (func) => set({ selectedFunction: func }),

      setSearchQuery: (query) => set({ searchQuery: query }),

      toggleDarkMode: () => set((state) => {
        const newDarkMode = !state.isDarkMode;
        if (newDarkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        return { isDarkMode: newDarkMode };
      }),

      addToRecentlyViewed: (messageId) => set((state) => {
        const filtered = state.recentlyViewed.filter(id => id !== messageId);
        const updated = [messageId, ...filtered].slice(0, MAX_RECENT_ITEMS);
        return { recentlyViewed: updated };
      }),

      toggleFavorite: (messageId) => set((state) => {
        const isFavorite = state.favorites.includes(messageId);
        const updated = isFavorite
          ? state.favorites.filter(id => id !== messageId)
          : [...state.favorites, messageId];
        return { favorites: updated };
      }),

      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarWidth: (width) => set({
        sidebarWidth: Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, width))
      }),
    }),
    {
      name: 'secs-reference-storage',
      partialize: (state) => ({
        isDarkMode: state.isDarkMode,
        recentlyViewed: state.recentlyViewed,
        favorites: state.favorites,
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarWidth: state.sidebarWidth,
      }),
    }
  )
);

// Initialize dark mode on app load
if (useAppStore.getState().isDarkMode) {
  document.documentElement.classList.add('dark');
}
